<script setup>
import { getAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { BubbleMenu as TiptapBubbleMenu } from '@tiptap/vue-3/menus';
import { computed, ref } from 'vue';
import TipTapToolbarButton from './TipTapToolbarButton';
import isExtEnableAndActive from './isExtEnableAndActive';
import { IMAGE_LAYOUTS, IMAGE_LAYOUT_BUTTONS } from './imageWithLayout';

const props = defineProps({
  editor: { type: Object, required: true },
  imageLayouts: { type: Array, default: () => IMAGE_LAYOUTS }
});

const layoutButtons = computed(() =>
  IMAGE_LAYOUT_BUTTONS.filter((button) => props.imageLayouts.includes(button.layout))
);

/**
 * True when the selection resolves to exactly one image, whether the author
 * clicked it (a NodeSelection) or dragged across it (a TextSelection spanning
 * the single leaf node).
 */
const isImageSelection = ({ selection, doc }) => {
  if (selection.node) return selection.node.type.name === 'image';
  if (selection.to - selection.from !== 1) return false;
  return doc.nodeAt(selection.from)?.type.name === 'image';
};

const resolveNodeType = (state) => {
  const { selection } = state;
  if (selection.to === selection.from || selection.empty) return undefined;

  // Images are classified before links and tables.
  if (isImageSelection(state)) return 'image';

  const isLink = isExtEnableAndActive(props.editor, 'link', state);
  const isTable = isExtEnableAndActive(props.editor, 'table', state);

  const isVideo = selection.node?.type.name === 'video';
  const isText = selection instanceof TextSelection;

  if (isTable) return 'table';
  if (isLink) return 'link';
  if (isVideo) return 'video';
  if (isText) return 'text';
  return undefined;
};

const nodeType = ref(undefined);
const activeImageLayout = ref(null);

const shouldShow = ({ state }) => {
  nodeType.value = resolveNodeType(state);
  activeImageLayout.value =
    nodeType.value === 'image' ? getAttributes(state, 'image').layout : null;

  return nodeType.value === 'image' || nodeType.value === 'text' || nodeType.value === 'link';
};

const isImageLayoutActive = (layout) => activeImageLayout.value === layout;
</script>

<template>
  <TiptapBubbleMenu :editor="editor" :should-show="shouldShow">
    <div v-if="nodeType === 'image'" class="bubble-menu">
      <TipTapToolbarButton
        v-for="button in layoutButtons"
        :key="button.layout"
        :icon="button.icon"
        :title="button.title"
        :is-active="isImageLayoutActive(button.layout)"
        size="x-small"
        @click="editor.chain().focus().setImageLayout(button.layout).run()"
      />
    </div>
    <div v-else class="bubble-menu">
      <TipTapToolbarButton
        icon="mdi-format-bold"
        title="Bold"
        :is-active="isExtEnableAndActive(editor, 'bold')"
        size="x-small"
        @click="editor.chain().focus().toggleBold().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-italic"
        title="Italic"
        :is-active="isExtEnableAndActive(editor, 'italic')"
        size="x-small"
        @click="editor.chain().focus().toggleItalic().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-underline"
        title="Underline"
        :is-active="isExtEnableAndActive(editor, 'underline')"
        size="x-small"
        @click="editor.chain().focus().toggleUnderline().run()"
      /><TipTapToolbarButton
        icon="mdi-format-list-bulleted"
        title="Bullet list"
        :is-active="isExtEnableAndActive(editor, 'bulletList')"
        size="x-small"
        @click="editor.chain().focus().toggleBulletList().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-list-numbered"
        title="Numbered list"
        :is-active="isExtEnableAndActive(editor, 'orderedList')"
        size="x-small"
        @click="editor.chain().focus().toggleOrderedList().run()"
      />
    </div>
  </TiptapBubbleMenu>
</template>

<style scoped>
.bubble-menu {
  background-color: white;
  border: 1px solid rgba(61, 37, 20, 0.05);
  border-radius: 1%;
  box-shadow:
    0px 12px 33px 0px rgba(0, 0, 0, 0.06),
    0px 3.618px 9.949px 0px rgba(0, 0, 0, 0.04);
  display: flex;
  padding: 0.2rem;
  gap: 2px;
}
.bubble-menu__buttons {
  border: 1px solid #e4e4e7;
}
</style>
